<script lang="ts">
	import type { Snippet } from 'svelte';

	interface Props {
		/** outlined = bordered (default), quiet = borderless, solid = accent fill, handle = square accent tab with a shadow (side-panel open/close). */
		variant?: 'outlined' | 'quiet' | 'solid' | 'handle';
		/** Shared control scale: xs 24px, sm 28px, md 40px. */
		size?: 'xs' | 'sm' | 'md';
		/** Fully rounded; otherwise the shared small radius. Handles are always square. */
		round?: boolean;
		disabled?: boolean;
		onclick?: () => void;
		/** Required: icon-only buttons have no other accessible name. */
		'aria-label': string;
		/** Native hover tooltip for icon-only buttons; rendered only when set. */
		title?: string;
		/** Invokes a popover by id (Popover API). */
		popovertarget?: string;
		'aria-describedby'?: string;
		'aria-expanded'?: boolean;
		'aria-controls'?: string;
		/** CSS anchor name, so an anchored popover can position against this button. */
		anchorName?: string;
		children: Snippet;
	}

	const {
		variant = 'outlined',
		size = 'md',
		round = true,
		disabled = false,
		children,
		onclick,
		'aria-label': ariaLabel,
		title,
		popovertarget,
		'aria-describedby': ariaDescribedby,
		'aria-expanded': ariaExpanded,
		'aria-controls': ariaControls,
		anchorName
	}: Props = $props();
</script>

<button
	type="button"
	class="button {variant} {size}"
	class:round={round && variant !== 'handle'}
	{disabled}
	{onclick}
	{title}
	{popovertarget}
	aria-label={ariaLabel}
	aria-describedby={ariaDescribedby}
	aria-expanded={ariaExpanded}
	aria-controls={ariaControls}
	style:anchor-name={anchorName}>
	{@render children()}
</button>

<style>
	.button {
		display: flex;
		flex-shrink: 0;
		justify-content: center;
		align-items: center;
		transition:
			background-color 0.15s ease-out,
			border-color 0.15s ease-out,
			color 0.15s ease-out;
		cursor: pointer;
		border: 2px solid var(--text-color);
		border-radius: var(--radius);
		background: var(--surface);
		padding: 0;
		width: var(--height-button);
		height: var(--height-button);
		color: var(--text-color);
		font-weight: var(--weight-bold);
		font-size: var(--font-size-sm);
		line-height: 1;

		&.sm {
			width: var(--height-button-sm);
			height: var(--height-button-sm);
		}

		&.xs {
			width: var(--target-min);
			height: var(--target-min);
		}

		&.round {
			border-radius: var(--radius-full);
		}

		&:hover:not(:disabled) {
			background-color: var(--hover-surface);
		}

		&.quiet {
			border-color: transparent;
			background: transparent;
		}

		&.solid,
		&.handle {
			border-color: var(--accent);
			background-color: var(--accent);
			color: var(--accent-contrast);

			&:hover:not(:disabled) {
				border-color: var(--accent-hover);
				background-color: var(--accent-hover);
			}
		}

		&.handle {
			box-shadow: var(--shadow-md);
		}

		&:disabled {
			cursor: not-allowed;
			color: var(--disabled-text);

			&.outlined {
				border-color: var(--disabled-text);
			}
		}

		:global(svg) {
			width: 60%;
			height: 60%;
		}
	}
</style>
